import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SEOScoreWidget from '../components/SEOScoreWidget';
import { blogAPI } from '../../services/api';
import { analyzeSEO } from '../../utils/seoAnalyzer';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ArchiveBoxIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const BlogPostsPage = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImage: '',
    author: 'LionTek Team',
    category: 'Technology',
    tags: [],
    published: false,
    readTime: 5,
    seoTitle: '',
    seoDescription: '',
    scheduledAt: '',
    isScheduled: false,
    status: 'draft'
  });
  const [tagInput, setTagInput] = useState('');

  // Auto-save timer
  useEffect(() => {
    if (!editingPost || !showModal) return;

    const timer = setTimeout(() => {
      handleAutoSave();
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [formData, editingPost, showModal]);

  // SEO Analysis
  useEffect(() => {
    if (formData.title || formData.content) {
      const analysis = analyzeSEO({
        title: formData.title,
        seoTitle: formData.seoTitle,
        description: formData.seoDescription,
        content: formData.content,
        tags: formData.tags,
        category: formData.category,
        featuredImage: formData.featuredImage
      });
      setSeoAnalysis(analysis);
    }
  }, [formData.title, formData.seoTitle, formData.seoDescription, formData.content, formData.tags, formData.category, formData.featuredImage]);

  // Fetch blog posts
  const { data: blogPostsData, isLoading } = useQuery(
    ['blogPosts', { status: statusFilter === 'all' ? undefined : statusFilter }],
    () => blogAPI.getAll({ 
      status: statusFilter === 'all' ? undefined : statusFilter
    }).then(res => res.data)
  );

  // Fetch categories
  const { data: categories } = useQuery('blogCategories', () => 
    blogAPI.getCategories().then(res => res.data)
  );

  // Create mutation
  const createMutation = useMutation(
    (data) => {
      const payload = {
        ...data,
        seoScore: seoAnalysis?.totalScore || 0,
        wordCount: seoAnalysis?.wordCount || 0,
        readingLevel: seoAnalysis?.readability?.level || 'intermediate',
        seoKeywords: data.tags?.join(',') || '',
        // Sanitize date fields
        scheduledAt: data.scheduledAt && data.scheduledAt !== '' && data.scheduledAt !== 'Invalid date'
          ? new Date(data.scheduledAt).toISOString()
          : null
      };
      return blogAPI.create(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blogPosts');
        setShowModal(false);
        resetForm();
        alert('Blog post created successfully!');
      },
      onError: (error) => {
        alert(error.response?.data?.error || 'Failed to create blog post');
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => {
      const payload = {
        ...data,
        seoScore: seoAnalysis?.totalScore || 0,
        wordCount: seoAnalysis?.wordCount || 0,
        readingLevel: seoAnalysis?.readability?.level || 'intermediate',
        seoKeywords: data.tags?.join(',') || '',
        // Sanitize date fields - convert to ISO string or null
        scheduledAt: data.scheduledAt && data.scheduledAt !== '' && data.scheduledAt !== 'Invalid date' 
          ? new Date(data.scheduledAt).toISOString() 
          : null,
        publishedAt: data.publishedAt && data.publishedAt !== '' && data.publishedAt !== 'Invalid date'
          ? new Date(data.publishedAt).toISOString()
          : undefined
      };
      return blogAPI.update(id, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blogPosts');
        setShowModal(false);
        resetForm();
        alert('Blog post updated successfully!');
      },
      onError: (error) => {
        console.error('Update error:', error.response?.data);
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.errors?.[0]?.msg || 
                        'Failed to update blog post';
        alert(errorMsg);
      }
    }
  );

  // Auto-save mutation
  const autoSaveMutation = useMutation(
    ({ id, data }) => {
      const payload = {
        ...data,
        seoScore: seoAnalysis?.totalScore || 0,
        wordCount: seoAnalysis?.wordCount || 0,
        readingLevel: seoAnalysis?.readability?.level || 'intermediate',
        seoKeywords: data.tags?.join(',') || '',
        // Sanitize date fields
        scheduledAt: data.scheduledAt && data.scheduledAt !== '' && data.scheduledAt !== 'Invalid date'
          ? new Date(data.scheduledAt).toISOString()
          : null
      };
      return blogAPI.autoSave(id, payload);
    },
    {
      onSuccess: (response) => {
        const savedTime = new Date(response.data.autoSavedAt);
        setAutoSaveStatus(`Saved at ${savedTime.toLocaleTimeString()}`);
        setTimeout(() => setAutoSaveStatus(''), 3000);
      },
      onError: () => {
        setAutoSaveStatus('Auto-save failed');
        setTimeout(() => setAutoSaveStatus(''), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => blogAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blogPosts');
        alert('Blog post deleted successfully!');
      },
      onError: (error) => {
        alert(error.response?.data?.error || 'Failed to delete blog post');
      }
    }
  );

  // Bulk update mutation
  const bulkUpdateMutation = useMutation(
    ({ ids, action }) => blogAPI.bulkUpdate({ ids, action }),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('blogPosts');
        setSelectedPosts([]);
        alert(response.data.message);
      },
      onError: (error) => {
        alert(error.response?.data?.error || 'Failed to perform bulk action');
      }
    }
  );

  const handleAutoSave = useCallback(() => {
    if (editingPost && formData.title && formData.content) {
      autoSaveMutation.mutate({ id: editingPost.id, data: formData });
    }
  }, [editingPost, formData, autoSaveMutation]);

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featuredImage: '',
      author: 'LionTek Team',
      category: 'Technology',
      tags: [],
      published: false,
      readTime: 5,
      seoTitle: '',
      seoDescription: '',
      scheduledAt: '',
      isScheduled: false,
      status: 'draft'
    });
    setEditingPost(null);
    setTagInput('');
    setSeoAnalysis(null);
    setAutoSaveStatus('');
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featuredImage: post.featuredImage || '',
      author: post.author,
      category: post.category,
      tags: post.tags || [],
      published: post.published,
      readTime: post.readTime,
      seoTitle: post.seoTitle || '',
      seoDescription: post.seoDescription || '',
      scheduledAt: post.scheduledAt ? new Date(post.scheduledAt).toISOString().slice(0, 16) : '',
      isScheduled: post.isScheduled || false,
      status: post.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingPost) {
      updateMutation.mutate({ id: editingPost.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingPost) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSelectPost = (id) => {
    setSelectedPosts(prev => 
      prev.includes(id) ? prev.filter(postId => postId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPosts.length === filteredPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(filteredPosts.map(post => post.id));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedPosts.length === 0) {
      alert('Please select at least one post');
      return;
    }

    let confirmMessage = '';
    switch (action) {
      case 'publish':
        confirmMessage = `Publish ${selectedPosts.length} post(s)?`;
        break;
      case 'archive':
        confirmMessage = `Archive ${selectedPosts.length} post(s)?`;
        break;
      case 'delete':
        confirmMessage = `Delete ${selectedPosts.length} post(s)? This action cannot be undone.`;
        break;
      default:
        return;
    }

    if (window.confirm(confirmMessage)) {
      bulkUpdateMutation.mutate({ ids: selectedPosts, action });
    }
  };

  // Filter posts
  const filteredPosts = blogPostsData?.blogPosts?.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || post.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) || [];

  if (isLoading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  const stats = {
    total: blogPostsData?.blogPosts?.length || 0,
    published: blogPostsData?.blogPosts?.filter(p => p.status === 'published').length || 0,
    scheduled: blogPostsData?.blogPosts?.filter(p => p.status === 'scheduled').length || 0,
    draft: blogPostsData?.blogPosts?.filter(p => p.status === 'draft').length || 0,
    archived: blogPostsData?.blogPosts?.filter(p => p.status === 'archived').length || 0
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || styles.draft}`}>
        {status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
            <p className="text-gray-600 mt-1">Manage your blog content with scheduling and SEO optimization</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            New Post
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Total</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Published</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.published}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Scheduled</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.scheduled}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Drafts</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.draft}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Archived</div>
            <div className="text-2xl font-bold text-gray-500 mt-1">{stats.archived}</div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedPosts.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedPosts.length} post(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkAction('publish')}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  Publish All
                </button>
                <button
                  onClick={() => handleBulkAction('archive')}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                >
                  Archive All
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories?.map(cat => (
                <option key={cat.category} value={cat.category}>
                  {cat.category} ({cat.count})
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Drafts</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>

            {/* Select All Checkbox */}
            <div className="flex items-center px-4 py-2">
              <input
                type="checkbox"
                id="selectAll"
                checked={selectedPosts.length === filteredPosts.length && filteredPosts.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="selectAll" className="ml-2 text-sm font-medium text-gray-700">
                Select All
              </label>
            </div>
          </div>
        </div>

        {/* Blog Posts Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={selectedPosts.length === filteredPosts.length && filteredPosts.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SEO Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No blog posts found
                    </td>
                  </tr>
                ) : (
                  filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedPosts.includes(post.id)}
                          onChange={() => handleSelectPost(post.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {post.featuredImage && (
                            <img
                              src={post.featuredImage}
                              alt={post.title}
                              className="w-12 h-12 rounded object-cover mr-3"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{post.title}</div>
                            <div className="text-sm text-gray-500">{post.excerpt?.substring(0, 60)}...</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{post.category}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{post.author}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(post.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm font-semibold ${
                            post.seoScore >= 80 ? 'text-green-600' :
                            post.seoScore >= 60 ? 'text-yellow-600' :
                            post.seoScore >= 40 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {post.seoScore || 0}/100
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {post.scheduledAt ? (
                          <div className="flex items-center gap-1 text-sm text-blue-600">
                            <ClockIcon className="w-4 h-4" />
                            {new Date(post.scheduledAt).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(post)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                            className="text-green-600 hover:text-green-900"
                            title="View"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-6xl w-full m-4 max-h-[90vh] flex flex-col">
              {/* Fixed Header */}
              <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                  </h2>
                  {autoSaveStatus && (
                    <p className="text-sm text-green-600 mt-1">{autoSaveStatus}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable Content */}
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content - 2/3 */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter post title"
                        />
                      </div>

                      {/* Slug */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Slug <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="slug"
                          value={formData.slug}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="post-url-slug"
                        />
                      </div>

                      {/* Excerpt */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Excerpt <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="excerpt"
                          value={formData.excerpt}
                          onChange={handleInputChange}
                          required
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Brief description of the post"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          {formData.excerpt.length}/500 characters
                        </p>
                      </div>

                      {/* Content */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="content"
                          value={formData.content}
                          onChange={handleInputChange}
                          required
                          rows={12}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="Write your blog post content here..."
                        />
                      </div>

                      {/* SEO Fields */}
                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Settings</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              SEO Title
                            </label>
                            <input
                              type="text"
                              name="seoTitle"
                              value={formData.seoTitle}
                              onChange={handleInputChange}
                              maxLength={60}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Optimized title for search engines"
                            />
                            <p className="text-sm text-gray-500 mt-1">
                              {formData.seoTitle.length}/60 characters (optimal: 50-60)
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Meta Description
                            </label>
                            <textarea
                              name="seoDescription"
                              value={formData.seoDescription}
                              onChange={handleInputChange}
                              rows={3}
                              maxLength={160}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="SEO-optimized description"
                            />
                            <p className="text-sm text-gray-500 mt-1">
                              {formData.seoDescription.length}/160 characters (optimal: 120-160)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sidebar - 1/3 */}
                    <div className="space-y-6">
                      {/* SEO Score Widget */}
                      {seoAnalysis && <SEOScoreWidget seoAnalysis={seoAnalysis} />}

                      {/* Publishing Options */}
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900">Publishing</h3>
                        
                        {/* Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="draft">Draft</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>

                        {/* Schedule Date/Time */}
                        {formData.status === 'scheduled' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                              <ClockIcon className="w-4 h-4" />
                              Schedule For
                            </label>
                            <input
                              type="datetime-local"
                              name="scheduledAt"
                              value={formData.scheduledAt}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  scheduledAt: e.target.value,
                                  isScheduled: true
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        )}

                        {/* Published Checkbox */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="published"
                            name="published"
                            checked={formData.published}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="published" className="ml-2 text-sm font-medium text-gray-700">
                            Publish immediately
                          </label>
                        </div>
                      </div>

                      {/* Post Details */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Featured Image URL
                          </label>
                          <input
                            type="text"
                            name="featuredImage"
                            value={formData.featuredImage}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Author
                          </label>
                          <input
                            type="text"
                            name="author"
                            value={formData.author}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category
                          </label>
                          <input
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Read Time (minutes)
                          </label>
                          <input
                            type="number"
                            name="readTime"
                            value={formData.readTime}
                            onChange={handleInputChange}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Tags */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tags
                          </label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              placeholder="Add tag..."
                            />
                            <button
                              type="button"
                              onClick={handleAddTag}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              Add
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {formData.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTag(tag)}
                                  className="hover:text-blue-900"
                                >
                                  <XMarkIcon className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fixed Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-2">
                    {editingPost && (
                      <button
                        type="button"
                        onClick={handleAutoSave}
                        disabled={autoSaveMutation.isLoading}
                        className="px-4 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                      >
                        {autoSaveMutation.isLoading ? 'Saving...' : 'Save Draft'}
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {createMutation.isLoading || updateMutation.isLoading 
                        ? 'Saving...' 
                        : editingPost 
                        ? 'Update Post' 
                        : 'Create Post'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BlogPostsPage;
